/**
 * Simple in-memory token-bucket rate limiter.
 * Prevents runaway AI API calls from exhausting quota or budget.
 *
 * Usage:
 *   const limiter = new RateLimiter({ maxRequests: 5, windowMs: 60_000 });
 *   await limiter.acquire(); // throws if over limit
 */
export class RateLimiter {
  private timestamps: number[] = [];
  private readonly maxRequests: number;
  private readonly windowMs: number;

  constructor({ maxRequests, windowMs }: { maxRequests: number; windowMs: number }) {
    this.maxRequests = maxRequests;
    this.windowMs = windowMs;
  }

  acquire(): void {
    const now = Date.now();
    this.timestamps = this.timestamps.filter(t => now - t < this.windowMs);

    if (this.timestamps.length >= this.maxRequests) {
      const oldest = this.timestamps[0];
      const retryAfter = Math.ceil((this.windowMs - (now - oldest)) / 1000);
      throw new Error(
        `Rate limit exceeded. You can make at most ${this.maxRequests} AI requests per ${this.windowMs / 1000}s. ` +
        `Please wait ${retryAfter}s before trying again.`
      );
    }

    this.timestamps.push(now);
  }
}

// Shared limiter for all Gemini calls: 10 requests per 60 seconds per browser session
export const geminiRateLimiter = new RateLimiter({ maxRequests: 10, windowMs: 60_000 });
